import React, { useEffect, useState, useRef, createElement } from 'react';
import { CheckIcon, ArrowRightIcon, BookIcon, SparklesIcon } from 'lucide-react';
import { SEOHead } from './SEOHead';
import { createPricingSchema } from '../utils/structuredData';
import { useStory } from './StoryContext';
import { useNavigate } from 'react-router-dom';
export const PricingPage = () => {
  const {
    isLoggedIn
  } = useStory();
  const navigate = useNavigate();
  const subscriptionContainerRef = useRef<HTMLDivElement>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeInitialized, setStripeInitialized] = useState(false);
  // Add Stripe script when component mounts
  useEffect(() => {
    let scriptElement: HTMLScriptElement | null = null;
    const loadStripeScript = () => {
      if (!isLoggedIn) {
        setIsStripeLoading(false);
        return;
      }
      // Check if script is already loaded
      if (document.querySelector('script[src*="buy-button.js"]')) {
        console.log('Stripe script already loaded, initializing button');
        setIsStripeLoading(false);
        initializeStripeButton();
        return;
      }
      console.log('Loading Stripe script...');
      setIsStripeLoading(true);
      setStripeError(null);
      scriptElement = document.createElement('script');
      scriptElement.src = 'https://js.stripe.com/v3/buy-button.js';
      scriptElement.async = true;
      scriptElement.crossOrigin = 'anonymous';
      scriptElement.onload = () => {
        console.log('Stripe script loaded successfully');
        setIsStripeLoading(false);
        initializeStripeButton();
      };
      scriptElement.onerror = error => {
        console.error('Failed to load Stripe script:', error);
        setIsStripeLoading(false);
        setStripeError('Failed to load payment system');
      };
      document.body.appendChild(scriptElement);
    };
    const initializeStripeButton = () => {
      if (!subscriptionContainerRef.current) {
        console.error('Subscription container ref not found');
        setStripeError('Container not found');
        return;
      }
      try {
        // Clear container first
        subscriptionContainerRef.current.innerHTML = '';
        // Create the button element
        const subscriptionButton = document.createElement('stripe-buy-button');
        subscriptionButton.setAttribute('buy-button-id', 'buy_btn_1RSQ6VGXXgnFm4qoMgfqYpD7');
        subscriptionButton.setAttribute('publishable-key', 'pk_live_51RQxIGGXXgnFm4qoXWD8eqed14BNE4nN1HKkw6TKHRfDCqQdwQMwZN4QYMLOIS0zP3QV82rcibjnRocq0ixnJnL3003wAeol5f');
        console.log('Appending Stripe button to container');
        subscriptionContainerRef.current.appendChild(subscriptionButton);
        setStripeInitialized(true);
      } catch (err) {
        console.error('Error creating subscription Stripe button:', err);
        setStripeError('Failed to initialize subscription button');
      }
    };
    // Load the script
    loadStripeScript();
    // Cleanup function
    return () => {
      if (scriptElement && document.body.contains(scriptElement)) {
        document.body.removeChild(scriptElement);
      }
    };
  }, [isLoggedIn]);
  // Add referral tracking script with proper duplicate prevention
  useEffect(() => {
    const initializeReferralTracking = () => {
      try {
        // Check if PromoteKit script already exists
        if (document.querySelector('script[data-promotekit]')) {
          console.log('PromoteKit script already loaded');
          return;
        }
        // Check if PromoteKit is already available globally
        if (window.promotekit_referral) {
          console.log('PromoteKit already initialized');
          return;
        }
        // Create PromoteKit script
        const promoteKitScript = document.createElement('script');
        promoteKitScript.src = 'https://cdn.promotekit.com/promotekit.js';
        promoteKitScript.async = true;
        promoteKitScript.setAttribute('data-promotekit', '20fab87b-f678-4888-a763-ebb32cc937ff');
        promoteKitScript.onerror = () => console.error('Failed to load PromoteKit script');
        promoteKitScript.onload = () => {
          setTimeout(() => {
            try {
              // Apply referral tracking to Stripe links and elements
              const referralId = window.promotekit_referral;
              if (referralId) {
                document.querySelectorAll('a[href^="https://buy.stripe.com/"]').forEach(link => {
                  const oldBuyUrl = link.getAttribute('href');
                  if (oldBuyUrl && !oldBuyUrl.includes('client_reference_id')) {
                    const newBuyUrl = oldBuyUrl + '?client_reference_id=' + referralId;
                    link.setAttribute('href', newBuyUrl);
                  }
                });
                document.querySelectorAll('[pricing-table-id]').forEach(element => {
                  element.setAttribute('client-reference-id', referralId);
                });
                document.querySelectorAll('[buy-button-id]').forEach(element => {
                  element.setAttribute('client-reference-id', referralId);
                });
              }
            } catch (err) {
              console.error('Error applying referral tracking:', err);
            }
          }, 1500);
        };
        document.head.appendChild(promoteKitScript);
        // Cleanup function
        return () => {
          try {
            if (document.head.contains(promoteKitScript)) {
              document.head.removeChild(promoteKitScript);
            }
          } catch (err) {
            console.error('Error cleaning up PromoteKit script:', err);
          }
        };
      } catch (err) {
        console.error('Error initializing referral tracking:', err);
      }
    };
    const cleanup = initializeReferralTracking();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);
  const pricingStructuredData = createPricingSchema();
  return <>
      <SEOHead title="Story Credits - Create Your Own Storybook" description="Create personalized AI-powered children's stories for just $4 for 5 books. Turn your child's imagination into beautifully illustrated storybooks instantly!" canonical="https://storybunny.ai/pricing" structuredData={pricingStructuredData} data-id="element-817" />
      <div className="min-h-screen bg-[#FFFBF6] py-16 px-4" data-id="element-818">
        <div className="max-w-6xl mx-auto text-center" data-id="element-819">
          <h1 className="text-3xl font-bold text-[#6B4B3E] mb-8" data-id="element-820">
            Story Credits
          </h1>
          {/* Pricing Cards Container */}
          <div className="max-w-md mx-auto mb-12" data-id="element-821">
            {/* 5 Story Credits Card */}
            <div className="bg-white rounded-3xl shadow-lg p-8 h-full" data-id="element-822">
              <div className="flex items-center justify-center mb-4" data-id="element-823">
                <BookIcon className="w-10 h-10 text-[#D4A088] mr-3" data-id="element-824" />
                <h2 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-825">
                  5 Story Credits
                </h2>
              </div>
              <div className="text-4xl font-bold text-[#6B4B3E] mb-2" data-id="element-826">$4</div>
              <div className="text-sm text-[#6B4B3E]/60 mb-8" data-id="element-827">
                Less than $1 per book
              </div>
              <div className="space-y-4 max-w-md mx-auto mb-8" data-id="element-828">
                <div className="flex items-start gap-3" data-id="element-829">
                  <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-830" />
                  <span className="text-left text-lg text-[#6B4B3E]" data-id="element-831">
                    Create 5 custom storybooks with beautiful illustrations
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-832">
                  <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-833" />
                  <span className="text-left text-lg text-[#6B4B3E]" data-id="element-834">
                    Personalize each story with your child's name and
                    preferences
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-835">
                  <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-836" />
                  <span className="text-left text-lg text-[#6B4B3E]" data-id="element-837">
                    All stories are saved in your personal library forever
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-838">
                  <CheckIcon className="w-6 h-6 text-[#D4A088] flex-shrink-0 mt-1" data-id="element-839" />
                  <span className="text-left text-lg text-[#6B4B3E]" data-id="element-840">
                    Option to share your stories in the community garden
                  </span>
                </div>
              </div>
              <div className="flex justify-center mt-auto" data-id="element-841">
                {isLoggedIn ? <a href="https://buy.stripe.com/6oU28rgVi8k5elr2be7ss02" className="px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors flex items-center justify-center" target="_blank" rel="noopener noreferrer" data-id="element-842">
                    <span data-id="element-843">Purchase 5 Story Credits</span>
                    <ArrowRightIcon className="ml-2" size={20} data-id="element-844" />
                  </a> : <button onClick={() => navigate('/create-account')} className="px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors flex items-center justify-center" data-id="element-845">
                    <span data-id="element-846">Create Account to Purchase</span>
                    <ArrowRightIcon className="ml-2" size={20} data-id="element-847" />
                  </button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
};